import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { Area, AreaChart, Label, Tooltip, XAxis, YAxis } from "recharts";
import { useAppSelector } from "../../app/hooks";
import { ANALYZE_GRADING_PRICE, tokenOptions } from "../../backend/graphql";
import { formatPrice, formatPricePerCarat } from "../../tools";
import GroupTitle from "../widgets/GroupTitle";
import SectionTitle from "../widgets/SectionTitle";

const CHART_WIDTH = 320;
const CHART_HEIGHT = 320;

const percent = (share: number) => Math.round(share * 100) + "%";

// The price axis's ticks: 1, 2 and 5 of each power of ten from 1,000 $ that fall within the curve,
// so on the log scale they come evenly and read as round thousands.
const priceTicks = (curve: { price: number }[]) => {
  const low = Math.min(...curve.map((point) => point.price));
  const high = Math.max(...curve.map((point) => point.price));
  const ticks = [];
  for (let power = 1000; power <= high; power *= 10)
    for (const step of [1, 2, 5])
      if (step * power >= low && step * power <= high) ticks.push(step * power);
  return ticks;
};

// The point hovered on the curve: its price in total and per carat, and how much of the stone's
// likely prices lie below and above it. The curve's own value has no unit worth showing: it only
// says how central a price is to the colors.
const PointTooltip = ({
  active,
  payload,
  carat,
}: {
  active?: boolean;
  payload?: any[];
  carat: number;
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Box
      sx={{
        backgroundColor: "rgba(97, 97, 97, 0.92)",
        borderRadius: "4px",
        color: "white",
        px: 1,
        py: 0.5,
      }}
    >
      <Typography variant="subtitle2">
        {formatPrice(point.price)} · {formatPricePerCarat(point.price / carat)}
      </Typography>
      <Typography variant="body2">
        {percent(point.below)} below · {percent(1 - point.below)} above
      </Typography>
    </Box>
  );
};

// A row's label and values, as the header's SpecRow draws them.
const labelSx = { fontSize: "0.875rem", color: "text.secondary", py: "3px" };
const valueSx = {
  fontSize: "0.9375rem",
  fontWeight: 500,
  textAlign: "right",
  py: "3px",
};

// A row of the section's grid: its label, then a price — or a range, low and high — per carat and
// in total. The prices come in total; the stone's carat divides them.
const PriceRow = ({
  label,
  totals,
  carat,
  sx,
}: {
  label: string;
  totals: number[];
  carat: number;
  sx?: object;
}) => (
  <>
    <Typography sx={{ ...labelSx, ...sx }}>{label}</Typography>
    <Typography sx={{ ...valueSx, ...sx }}>
      {totals.map((total) => formatPricePerCarat(total / carat)).join(" – ")}
    </Typography>
    <Typography sx={{ ...valueSx, ...sx }}>
      {totals.map((total) => formatPrice(total)).join(" – ")}
    </Typography>
  </>
);

// A change of price as a signed percentage, green for a gain, red for a loss.
export const Change = ({ share }: { share: number }) => (
  <Box
    component="span"
    sx={{ color: share >= 0 ? "success.main" : "error.main" }}
  >
    {(share >= 0 ? "+" : "−") + Math.round(Math.abs(share) * 100) + "%"}
  </Box>
);

// A group of rows opens with its title, as the header's groups do, level with the two price
// columns' headings.
const PriceGroup = ({ title }: { title: string }) => (
  <>
    <GroupTitle>{title}</GroupTitle>
    {["Per carat", "Total"].map((heading) => (
      <Typography
        key={heading}
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: "right" }}
      >
        {heading}
      </Typography>
    ))}
  </>
);

// The price a stone of these likely colors fetches: price upward, how likely each price is across.
// The curve is the worker's: for every likely color, 100% at the P50 of its pricing sheet (at the
// stone's clarity and carat) and falling evenly on either side to 0 at P0 and P100, weighed by the
// color's probability.
const PriceCurve = ({ pricing, carat }: { pricing: any; carat: number }) => (
  <AreaChart
    width={CHART_WIDTH}
    height={CHART_HEIGHT}
    data={pricing.curve}
    layout="vertical"
    margin={{ top: 8, right: 16, bottom: 16, left: 16 }}
  >
    {/* No grid and no axis line across: the curve and its price axis only. */}
    <XAxis
      type="number"
      dataKey="probability"
      domain={[0, 1]}
      tick={false}
      axisLine={false}
    >
      <Label value="Likelihood" position="insideBottom" offset={-4} />
    </XAxis>
    {/* On a log scale: the likely colors' sheets run over a thousandfold, from a few hundred to a
        million dollars at their ends. */}
    <YAxis
      type="number"
      dataKey="price"
      scale="log"
      // A vertical chart runs its y axis downward, as for a list of categories: turned back, so
      // the higher price is on top.
      reversed
      domain={["dataMin", "dataMax"]}
      ticks={priceTicks(pricing.curve)}
      interval={0}
      tickFormatter={formatPrice}
      // Wide enough for the longest tick, "1,000,000 $", on one line.
      width={100}
    />
    <Area
      dataKey="probability"
      type="monotone"
      stroke="#118C67"
      fill="#A6CFC3"
      isAnimationActive={false}
    />
    {/* Hovering draws a line across at the price pointed at, and gives the price and the
        shares below and above it. */}
    <Tooltip
      cursor={{ stroke: "#616161" }}
      isAnimationActive={false}
      content={(props: any) => <PointTooltip {...props} carat={carat} />}
    />
  </AreaChart>
);

// The pricing sections of the grading analysis: for a stone GIA has not certified, the price its
// likely colors give it; for a certified one, the same curve and what a regrading could bring
// against the price of its own color.
const GradingPrice = ({
  diamondID,
  certified,
  stoneColor,
  carat,
}: {
  diamondID: string;
  certified: boolean;
  stoneColor?: string;
  carat: number;
}) => {
  const token = useAppSelector((state) => state.auth.token);
  const [analyzeGradingPrice] = useMutation(
    ANALYZE_GRADING_PRICE,
    tokenOptions(token),
  );
  const [pricing, setPricing] = useState<any>(null);

  useEffect(() => {
    setPricing(null);
    analyzeGradingPrice({ variables: { id: diamondID } }).then((result) =>
      setPricing(result?.data?.analyzeGradingPrice ?? {}),
    );
  }, [analyzeGradingPrice, diamondID]);

  const title = certified ? "Regrading opportunity" : "Expected price";
  if (!pricing)
    return (
      <>
        <SectionTitle>{title}</SectionTitle>
        <Skeleton variant="rounded" width={CHART_WIDTH} height={CHART_HEIGHT} />
      </>
    );
  if (!pricing.colors?.length)
    return (
      <>
        <SectionTitle>{title}</SectionTitle>
        <Typography variant="body2" color="text.secondary">
          No price for the colors this stone may be
        </Typography>
      </>
    );

  const regrading = certified ? pricing.regrading : null;
  return (
    <>
      <SectionTitle>{title}</SectionTitle>
      <Stack
        direction="row"
        spacing={4}
        useFlexGap
        flexWrap="wrap"
        alignItems="flex-start"
        sx={{ mt: 1 }}
      >
        <PriceCurve pricing={pricing} carat={carat} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "max-content max-content max-content",
            columnGap: 3,
            alignItems: "baseline",
          }}
        >
          {regrading ? (
            <>
              {/* No price here: the regraded range is a change from the stone's own color's
                  theoretical price, the same per carat as in total. */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <GroupTitle>Regrading</GroupTitle>
              </Box>
              <Typography sx={labelSx}>Regraded</Typography>
              <Typography sx={{ ...valueSx, gridColumn: "2 / -1" }}>
                <Change share={regrading.lowest / regrading.current - 1} />
                {" / "}
                <Change share={regrading.highest / regrading.current - 1} />
              </Typography>
              <Typography sx={labelSx}>Chance of a better price</Typography>
              <Typography sx={{ ...valueSx, gridColumn: "2 / -1" }}>
                {percent(regrading.betterProbability)}
              </Typography>
            </>
          ) : (
            <>
              <PriceGroup title="Price" />
              <PriceRow
                label="Likely range"
                totals={[pricing.p25, pricing.p75]}
                carat={carat}
              />
            </>
          )}
          {/* Each likely color with the theoretical price it would give the stone — its sheet's
              P50 — in the final grading's order, the stone's own color picked out. */}
          <Divider sx={{ gridColumn: "1 / -1", mt: "14px", mb: "10px" }} />
          <PriceGroup title="Theoretical price by color" />
          {pricing.colors.map((color: any) => {
            const own = color.color === stoneColor;
            return (
              <PriceRow
                key={color.color}
                label={color.color}
                totals={[color.median]}
                carat={carat}
                sx={own ? { fontWeight: 700, color: "primary.main" } : {}}
              />
            );
          })}
        </Box>
      </Stack>
    </>
  );
};

export default GradingPrice;
